/**
 * mileage (Phibian Mileage). Team reads need `mileage:review`; approving needs
 * `mileage:approve`; policy and rates need `mileage:configure`.
 *
 * PRIVACY: the reviewer list contains only drives a worker classified as WORK,
 * by the workers the caller actually reaches — their own row plus their direct
 * reports, unless they hold `mileage:review_all`. A personal drive never reaches
 * an employer surface, in any shape. The server applies both rules, so this
 * client never has to redact anything and could not un-redact it if it tried.
 *
 * NO REVIEWER SURFACE CARRIES ROUTE GEOMETRY. `AdminTrip` has no `route` field
 * and never will; it carries `routePointCount` (how many readings the distance
 * was measured from) and `routeWithheld`. A worker's own `/mileage/me/...` reads
 * are unchanged and still carry every reading — their movement is theirs.
 *
 * `AdminTrip.detailRestricted` survives for the single-trip read, where a drive
 * can still be fetched by id and has to answer honestly that its detail is not
 * available.
 */
import { get, post, put } from './transport.js';
/** Every review state, in the server's order. Exported so tests can pin it. */
export const MILEAGE_REVIEW_STATES = [
    'none',
    'ready_for_review',
    'approved',
    'needs_correction',
    'reimbursed',
    'exported',
];
/**
 * `GET /mileage/trips` -> `{ trips, awaitingClassification }` (needs `mileage:review`).
 *
 * `trips` contains ONLY drives the worker classified as work, and only for the
 * workers the caller reaches. A personal drive is not here in any form: not
 * redacted, not counted, not measured. A drive by somebody else's report is not
 * here either. The server decides both, not this client and not the screen.
 *
 * `awaitingClassification` is how much driving nobody has answered for yet, so a
 * reviewer can chase it. It carries a count and a distance, never a route,
 * a time or a destination.
 */
export async function trips(filters = {}, options) {
    return get('/mileage/trips', { ...options, query: { ...filters } });
}
/**
 * `GET /mileage/exceptions` → `{ exceptions }` (needs `mileage:review`).
 *
 * Mileage that is stuck: unanswered drives, unapproved claims, and claims whose
 * capture was too patchy to trust. Carries no coordinates, even for drives the
 * worker has not classified.
 */
export async function exceptions(options) {
    const body = await get('/mileage/exceptions', options);
    return body.exceptions;
}
/** `GET /mileage/trips/:id` → `{ trip }` (needs `mileage:review`). */
export async function trip(id, options) {
    const body = await get(`/mileage/trips/${id}`, options);
    return body.trip;
}
/**
 * `POST /mileage/trips/:id/approve` (needs `mileage:approve`).
 *
 * Refused by the server when the approver is the driver, when the drive is not
 * awaiting review, or when no rate was in force on the day of the drive.
 */
export async function approve(id, input = {}, options) {
    return post(`/mileage/trips/${id}/approve`, input, options);
}
/** `POST /mileage/trips/:id/return` (needs `mileage:approve`). Reason required. */
export async function returnForCorrection(id, input, options) {
    return post(`/mileage/trips/${id}/return`, input, options);
}
/**
 * `POST /mileage/trips/:id/reimburse` (needs `mileage:approve`).
 *
 * Phibian moves no money. This records that an already-approved drive was paid
 * outside Phibian — who said so, and why — so it stops showing as owing. The
 * reimbursement amount and rate are untouched: what was approved is what was
 * paid.
 */
export async function reimburse(id, input, options) {
    return post(`/mileage/trips/${id}/reimburse`, input, options);
}
/**
 * `POST /mileage/exports` → `{ export, created }` (needs `mileage:export`).
 *
 * Assembles every approved-and-not-yet-exported drive in the pay period into
 * one payroll-neutral export and moves each drive on to `exported`. Retrying
 * the same `idempotencyKey` returns the existing run (`created: false`)
 * instead of paying the period twice.
 */
export async function createExport(input, options) {
    return post('/mileage/exports', input, options);
}
/** `GET /mileage/exports` → `{ exports }` (needs `mileage:export`). */
export async function listExports(options) {
    return get('/mileage/exports', options);
}
/** `GET /mileage/policy` → `{ policy, rates }` (needs `mileage:configure`). */
export async function policy(options) {
    return get('/mileage/policy', options);
}
/** `PUT /mileage/policy` → `{ policy }` (needs `mileage:configure`). */
export async function updatePolicy(input, options) {
    return put('/mileage/policy', input, options);
}
/**
 * `POST /mileage/rates` → `{ rate }` (needs `mileage:configure`).
 *
 * Opening a rate closes the previous open one. Rates are effective-dated so
 * approving last month's mileage still applies last month's rate; no statutory
 * figure is defaulted anywhere.
 *
 * `effectiveFrom` is a calendar day, 'YYYY-MM-DD' — the value of a date input,
 * sent as typed. Sending an instant instead hands the server a day that depends
 * on the sender's zone.
 */
export async function openRate(input, options) {
    return post('/mileage/rates', input, options);
}
//# sourceMappingURL=mileage.js.map